import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";

import { useConfirmationDialogContext } from "../../../../../../confirmation-dialog/ConfirmationDialogContext";
import { FieldWithInfo } from "../../../personal-data/components/field-with-info/FieldWithInfo";
import { formatPartnerName, useExpensePartner } from "./hooks/useExpensePartner";

const SECTION = "clients.client.debtSettlement.assetInventory.expensesTab.partner";

interface Props {
  clientId: string;
  isDirty: boolean;
}

export function ExpensePartnerSection({ clientId, isDirty }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { partner, searchError, isSearching, search, share, isSharing } = useExpensePartner(clientId);
  const [caseNumber, setCaseNumber] = useState("");
  const label = t(`${SECTION}.label`);

  const confirmShare = () => {
    if (!partner) {
      return;
    }
    const name = formatPartnerName(partner);
    showConfirmationDialog({
      dialogTitleText: t(`${SECTION}.confirmTitle`),
      dialogText: t(`${SECTION}.confirmText`, { name }),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (confirmed) {
          share(partner);
        }
      },
    });
  };

  return (
    <Stack gap={1}>
      <FieldWithInfo fieldLabel={label} info={t(`${SECTION}.info`)}>
        <Box display="flex" gap={1} alignItems="start">
          <TextField
            id="expenses-partner-case-number"
            label={label}
            size="small"
            value={caseNumber}
            error={!!searchError}
            helperText={searchError}
            onChange={(event) => setCaseNumber(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                search(caseNumber);
              }
            }}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={() => search(caseNumber)} disabled={isSearching || !caseNumber.trim()}>
            {t(`${SECTION}.search`)}
          </Button>
        </Box>
      </FieldWithInfo>
      {partner && (
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" role="status">
          <Typography>{t(`${SECTION}.sharedWith`, { name: formatPartnerName(partner) })}</Typography>
          <Button variant="contained" onClick={confirmShare} disabled={isDirty || isSharing}>
            {t(`${SECTION}.share`)}
          </Button>
          {isDirty && (
            <Typography variant="body2" color="text.secondary">
              {t(`${SECTION}.saveFirst`)}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
